<script setup lang="ts">
import { ref, computed, defineProps, defineEmits, onMounted, watch } from "vue";
import {
    Combobox,
    ComboboxInput,
    ComboboxButton,
    ComboboxOptions,
    ComboboxOption,
    TransitionRoot,
} from "@headlessui/vue";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/vue/20/solid";

const selected = ref<any>("");

const props = defineProps<{
    options: {
        value: number | string;
        label: string;
        rightCount?: number | string;
    }[];
    modelValue: number | string | undefined;
    inputClasses: string | undefined;
}>();

const emits = defineEmits(["update:modelValue"]);

let query = ref("");

let filtered = computed(() =>
    query.value === ""
        ? props.options
        : props.options.filter((person) =>
              person.label
                  .toLowerCase()
                  .replace(/\s+/g, "")
                  .includes(query.value.toLowerCase().replace(/\s+/g, ""))
          )
);

const updateModelValue = (value: number | string) => {
    emits("update:modelValue", value);
};

const selectedLabel = computed(() => {
    const selected = props.options.find(
        (option) => option.value === props.modelValue
    );
    return selected ? selected.label : "";
});

onMounted(() => {
    selected.value = props.modelValue;
});

watch(
    () => props.modelValue,
    (value) => {
        selected.value = value;
    }
);
</script>
<template>
    <div class="">
        <Combobox
            :model-value="props.modelValue"
            @update:model-value="updateModelValue"
        >
            <div class="relative mt-1">
                <div
                    class="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left border focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm"
                    :class="props.inputClasses"
                >
                    <ComboboxInput
                        class="w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
                        :displayValue="(person) => selectedLabel"
                        @change="query = $event.target.value"
                    />
                    <ComboboxButton
                        class="absolute inset-y-0 right-0 flex items-center pr-2"
                    >
                        <ChevronUpDownIcon
                            class="h-5 w-5 text-gray-400"
                            aria-hidden="true"
                        />
                    </ComboboxButton>
                </div>
                <TransitionRoot
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    @after-leave="query = ''"
                >
                    <ComboboxOptions
                        class="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-100"
                    >
                        <div
                            v-if="filtered.length === 0 && query !== ''"
                            class="relative cursor-default select-none py-2 px-4 text-gray-700"
                        >
                            Nothing found.
                        </div>

                        <ComboboxOption
                            v-for="item in filtered"
                            as="template"
                            :key="item.value"
                            :value="item.value"
                            v-slot="{ selected, active }"
                        >
                            <li
                                class="relative cursor-default select-none py-2 pl-10 pr-4"
                                :class="{
                                    'bg-teal-600 text-white': active,
                                    'text-gray-900': !active,
                                }"
                            >
                                <div
                                    class="flex items-center justify-between gap-3"
                                >
                                    <span
                                        class="block"
                                        :class="{
                                            'font-medium': selected,
                                            'font-normal': !selected,
                                        }"
                                    >
                                        {{ item.label }}
                                    </span>

                                    <span
                                        v-if="item.rightCount !== undefined"
                                        class="shrink-0 text-xs"
                                        :class="{
                                            'text-white/90': active,
                                            'text-gray-500': !active,
                                        }"
                                    >
                                        {{ item.rightCount }}
                                    </span>
                                </div>

                                <span
                                    v-if="selected"
                                    class="absolute inset-y-0 left-0 flex items-center pl-3"
                                    :class="{
                                        'text-white': active,
                                        'text-teal-600': !active,
                                    }"
                                >
                                    <CheckIcon
                                        class="h-5 w-5"
                                        aria-hidden="true"
                                    />
                                </span>
                            </li>
                        </ComboboxOption>
                    </ComboboxOptions>
                </TransitionRoot>
            </div>
        </Combobox>
    </div>
</template>
